import React, { useMemo, useState } from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { DollarSign, TrendingDown, TrendingUp } from 'lucide-react';
import { Card, EmptyState, Skeleton } from '../ui';
import { formatMoney } from './formatters';
import {
  TRANSACTION_RANGES,
  buildDailySeries,
  buildLifetimeTotals,
  hasSettledTransactions,
} from './useCashMovementData';

const TICK_STYLE = { fontSize: 11, fill: '#94a3b8' };

const CustomTooltip = ({ active, payload, currency }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="rounded-lg border border-ink-200 bg-white shadow-popover px-3.5 py-2.5">
      <p className="text-2xs font-semibold uppercase tracking-wider text-ink-400">{point.label}</p>
      <p className="mt-1 text-sm font-semibold text-ink-900 tnum">{formatMoney(point.volume, currency)}</p>
      <div className="mt-1.5 space-y-0.5">
        <p className="text-xs text-ink-500 tnum">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent-500 mr-1.5" />
          Deposits {formatMoney(point.deposits, currency)}
        </p>
        <p className="text-xs text-ink-500 tnum">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-ink-300 mr-1.5" />
          Withdrawals {formatMoney(point.withdrawals, currency)}
        </p>
      </div>
    </div>
  );
};

// Real cash-movement data — sourced live from platform-financials-service via
// the partner gateway (the same deposits/withdrawals endpoints the
// Cash In/Out page uses). Scoped to one real currency (picked by the caller
// from the approved transactions) and counting approved transactions only; see
// CurrencyBreakdownCard for the full multi-currency picture.
const CashMovementChart = ({ loading, deposits, withdrawals, baseCurrency = 'AED' }) => {
  const [range, setRange] = useState('30d');
  const rangeDef = TRANSACTION_RANGES.find((r) => r.value === range) || TRANSACTION_RANGES[1];

  const series = useMemo(
    () => buildDailySeries(deposits, withdrawals, rangeDef.days, baseCurrency),
    [deposits, withdrawals, rangeDef.days, baseCurrency],
  );

  const hasData = useMemo(
    () => hasSettledTransactions(deposits, withdrawals, baseCurrency),
    [deposits, withdrawals, baseCurrency],
  );

  const lifetime = useMemo(
    () => buildLifetimeTotals(deposits, withdrawals, baseCurrency),
    [deposits, withdrawals, baseCurrency],
  );

  const { total, totalDeposits, totalWithdrawals, deltaPct } = useMemo(() => {
    const totalVolume = series.reduce((sum, p) => sum + p.volume, 0);
    const dep = series.reduce((sum, p) => sum + p.deposits, 0);
    const wd = series.reduce((sum, p) => sum + p.withdrawals, 0);
    const mid = Math.floor(series.length / 2) || 1;
    const firstHalf = series.slice(0, mid).reduce((sum, p) => sum + p.volume, 0) / mid;
    const secondHalf = series.slice(mid).reduce((sum, p) => sum + p.volume, 0) / (series.length - mid || 1);
    const delta = firstHalf > 0 ? ((secondHalf - firstHalf) / firstHalf) * 100 : 0;
    return { total: totalVolume, totalDeposits: dep, totalWithdrawals: wd, deltaPct: delta };
  }, [series]);

  const tickInterval = rangeDef.value === '7d' ? 0 : rangeDef.value === '30d' ? 4 : 12;
  const isUp = deltaPct >= 0;

  if (loading) {
    return (
      <Card>
        <Skeleton width="40%" height="12px" />
        <Skeleton className="mt-3" width="30%" height="32px" />
        <Skeleton className="mt-6" width="100%" height="200px" />
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-medium text-ink-500 uppercase tracking-wider">Cash movement · {baseCurrency}</p>
          <div className="mt-1.5 flex items-baseline gap-2.5">
            <p className="text-3xl font-semibold tracking-tight text-ink-900 tnum">{formatMoney(total, baseCurrency, { compact: true })}</p>
            {hasData && (
              <span
                className={`inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded ${
                  isUp ? 'text-success-700 bg-success-50' : 'text-danger-700 bg-danger-50'
                }`}
              >
                {isUp ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                {Math.abs(deltaPct).toFixed(1)}%
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-ink-500 tnum">
            Last {rangeDef.days} days: Deposits {formatMoney(totalDeposits, baseCurrency, { compact: true })} · Withdrawals{' '}
            {formatMoney(totalWithdrawals, baseCurrency, { compact: true })}
          </p>
          {hasData && (
            <p className="mt-1 text-xs text-ink-400 tnum">
              To date: {formatMoney(lifetime.totalDeposited, baseCurrency, { compact: true })} deposited ·{' '}
              {formatMoney(lifetime.totalWithdrawn, baseCurrency, { compact: true })} withdrawn
            </p>
          )}
        </div>

        <div className="inline-flex rounded-md border border-ink-200 bg-ink-50 p-0.5">
          {TRANSACTION_RANGES.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => setRange(r.value)}
              className={`px-3 py-1 rounded-[5px] text-xs font-medium transition-colors ${
                range === r.value ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-700'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {hasData ? (
        <div className="mt-6 h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="volumeGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity={0.22} />
                  <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="#e2e8f0" strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                tick={TICK_STYLE}
                interval={tickInterval}
                minTickGap={24}
              />
              <Tooltip content={<CustomTooltip currency={baseCurrency} />} cursor={{ stroke: '#cbd5e1', strokeWidth: 1 }} />
              <Area
                type="monotone"
                dataKey="volume"
                stroke="#4f46e5"
                strokeWidth={2}
                fill="url(#volumeGradient)"
                dot={false}
                activeDot={{ r: 3.5, fill: '#4f46e5', strokeWidth: 0 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="mt-4">
          <EmptyState
            icon={DollarSign}
            title="No approved transactions yet"
            description="Approved deposits and withdrawals will appear here once your customers start transacting."
          />
        </div>
      )}
    </Card>
  );
};

export default CashMovementChart;
